import type {
  AnonymityMode,
  CompletionStatus,
  SurveyDefinition,
  SurveyResponsesExport,
  SurveySummary,
} from '@voice2me/shared';
import { api } from '../api/client.js';

export type Vendor = {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  createdAt: string;
};
export type SurveyDetail = SurveySummary & { definition: SurveyDefinition };
export type RosterEntry = { email: string; addedAt: string };

export const adminApi = {
  vendors: () => api.get<Vendor[]>('/admin/vendors'),
  createVendor: (input: { name: string; slug: string }) =>
    api.post<Vendor>('/admin/vendors', input),
  updateVendorLogo: (vendorId: string, logoUrl: string | null) =>
    api.patch<Vendor>(`/admin/vendors/${vendorId}`, { logoUrl }),
  deleteVendor: (vendorId: string) => api.del<void>(`/admin/vendors/${vendorId}`),

  roster: (vendorId: string) => api.get<RosterEntry[]>(`/admin/vendors/${vendorId}/roster`),
  uploadRoster: (vendorId: string, input: { emails: string[]; replaceExisting: boolean }) =>
    api.put<{ total: number; added: number; removed: number }>(
      `/admin/vendors/${vendorId}/roster`,
      input,
    ),

  surveys: (vendorId?: string) =>
    api.get<SurveySummary[]>(`/admin/surveys${vendorId ? `?vendorId=${vendorId}` : ''}`),
  survey: (surveyId: string) => api.get<SurveyDetail>(`/admin/surveys/${surveyId}`),
  createSurvey: (input: {
    vendorId: string;
    slug: string;
    anonymityMode: AnonymityMode;
    definition: SurveyDefinition;
  }) => api.post<SurveySummary>('/admin/surveys', input),
  updateSurvey: (
    surveyId: string,
    input: {
      vendorId?: string;
      slug?: string;
      anonymityMode?: AnonymityMode;
      definition?: SurveyDefinition;
    },
  ) => api.patch<SurveySummary>(`/admin/surveys/${surveyId}`, input),
  publish: (surveyId: string) => api.post<SurveySummary>(`/admin/surveys/${surveyId}/publish`),
  close: (surveyId: string) => api.post<SurveySummary>(`/admin/surveys/${surveyId}/close`),
  clone: (surveyId: string) => api.post<SurveySummary>(`/admin/surveys/${surveyId}/clone`),
  remove: (surveyId: string) => api.del<void>(`/admin/surveys/${surveyId}`),
  responses: (surveyId: string) =>
    api.get<SurveyResponsesExport>(`/admin/surveys/${surveyId}/responses`),
  completion: (surveyId: string) =>
    api.get<CompletionStatus>(`/admin/surveys/${surveyId}/completion`),
};

/** Stable, readable ids. They are referenced by stored answers, so they must never be reused. */
export function newId(prefix: 'q' | 'opt' | 'sec'): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}
